import { create } from 'zustand'

import { env } from '@/shared/config'
import { registerReset } from '@/shared/lib/reset-registry'

import type { RealtimeCredentials } from './credentials'
import {
  clearStreamHandlers,
  dispatchStreamEvent,
  dispatchStreamReconnect,
} from './event-registry'

/**
 * The transport facade. Only this file knows the stream library — swap
 * Centrifugo below for a raw socket or SSE and nothing outside this folder
 * moves.
 *
 * The connection is created once at app level, above the router. A connection
 * owned by a route drops on navigation and silently loses events.
 */

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed'

type ConnectionState = { status: ConnectionStatus }

export const useConnectionStore = create<ConnectionState>(() => ({ status: 'idle' }))

const setStatus = (status: ConnectionStatus) => useConnectionStore.setState({ status })

/** Credentials are fetched per connect, never captured — the token lives 60s. */
type CredentialsGetter = () => Promise<RealtimeCredentials>

type StreamClient = { disconnect: () => void }

let client: StreamClient | null = null
/** Guards against a second connect while the first is still resolving. */
let opening = false
/** Distinguishes the first connect from a reconnect that needs a resync. */
let hasConnectedOnce = false
/** Bumped by `disconnect`, so an open still waiting on a token stops cleanly. */
let generation = 0

/**
 * Centrifuge only retries once it has an instance to retry with. If the very
 * first token fetch fails, nothing exists yet — without this loop a backend
 * blip at sign-in would leave the panel without live updates until a reload.
 */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000]

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

async function firstCredentials(
  getCredentials: CredentialsGetter,
  startedIn: number,
): Promise<RealtimeCredentials | null> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await getCredentials()
    } catch (error) {
      if (startedIn !== generation) return null
      console.error('realtime token fetch failed, retrying', error)
      await delay(RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)] ?? 30_000)
      if (startedIn !== generation) return null
    }
  }
}

function handleMessage(data: unknown) {
  // Normalization into a domain type happens in the feature's zod schema; the
  // transport only splits the envelope.
  if (typeof data !== 'object' || data === null || !('type' in data)) return
  const { type, payload } = data as { type: unknown; payload?: unknown }
  if (typeof type !== 'string') return
  dispatchStreamEvent(type, payload)
}

async function open(getCredentials: CredentialsGetter) {
  if (env.VITE_REALTIME_URL === undefined) return
  const startedIn = generation

  // Dynamic: the stream client and its protobuf codec stay out of the initial
  // bundle, which `build/budget-plugin` caps.
  const { Centrifuge } = await import('centrifuge')

  // The channel is only known from a token response, and a subscription
  // cannot be created without it.
  const first = await firstCredentials(getCredentials, startedIn)
  // A sign-out while the fetch was retrying wins: stay disconnected.
  if (first === null || startedIn !== generation) return

  const next = new Centrifuge(env.VITE_REALTIME_URL, {
    token: first.token,
    // Called again on every reconnect and before the current token expires.
    getToken: async () => (await getCredentials()).token,
  })

  next.on('connecting', () => setStatus(hasConnectedOnce ? 'reconnecting' : 'connecting'))
  next.on('connected', () => {
    setStatus('open')
    // Everything that happened during the gap is unrecoverable — features
    // refetch their snapshots instead of trusting the store.
    if (hasConnectedOnce) dispatchStreamReconnect()
    hasConnectedOnce = true
  })
  next.on('disconnected', () => setStatus('closed'))
  next.on('error', (context) => console.error('stream error', context.error.message))

  const subscription = next.newSubscription(first.channel)
  subscription.on('publication', (context) => handleMessage(context.data))
  subscription.subscribe()

  client = next
  next.connect()
}

export const realtime = {
  /** Called once from the composition root, after auth is wired. */
  connect(getCredentials: CredentialsGetter) {
    if (env.VITE_REALTIME_URL === undefined) return
    if (client !== null || opening) return

    opening = true
    setStatus('connecting')
    void open(getCredentials)
      .catch((error: unknown) => {
        // A failed token call or a refused socket is not fatal: the panel
        // still renders, just without live updates.
        console.error('stream connect failed', error)
        setStatus('closed')
      })
      .finally(() => {
        opening = false
      })
  },

  disconnect() {
    // Reconnection belongs to the stream client; disconnecting stops it. The
    // generation bump also stops an `open` still waiting on its first token.
    generation += 1
    client?.disconnect()
    client = null
    hasConnectedOnce = false
    setStatus('closed')
  },
}

registerReset(() => {
  realtime.disconnect()
  clearStreamHandlers()
  useConnectionStore.setState({ status: 'idle' })
})
